import { useEffect, useState, useCallback } from 'react';
import { Image, Trash2, Upload, RefreshCw, HardDrive, TrendingUp, CheckCircle, XCircle } from 'lucide-react';
import { apiFetch } from '../lib/api';

interface MediaFile { key: string; size: number; uploaded: string | null; url: string; }
interface MediaStats {
  total_uploads: number; total_deletes: number; active: number;
  current_files: number; range_uploads: number; range_deletes: number;
}

const STAT_RANGES = [
  { key: 'all', label: 'All time' },
  { key: '24h', label: 'Today' },
  { key: '7d', label: '7 days' },
  { key: '1m', label: '1 month' },
  { key: '1y', label: '1 year' },
];

const IMAGE_EXTS = new Set(['jpg','jpeg','png','gif','webp','svg','avif']);
const isImage = (key: string) => IMAGE_EXTS.has(key.split('.').pop()?.toLowerCase() || '');

function formatBytes(b: number) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export default function MediaLibrary() {
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState<MediaStats | null>(null);
  const [statsRange, setStatsRange] = useState('all');
  const [filter, setFilter] = useState<'all' | 'image' | 'other'>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await apiFetch('/api/admin/media');
    const data = await res.json();
    setFiles(data.objects || []);
    setLoading(false);
  }, []);

  const loadStats = useCallback(async () => {
    const res = await apiFetch(`/api/admin/media/stats?range=${statsRange}`);
    if (res.ok) { const d = await res.json(); setStats(d); }
  }, [statsRange]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadFiles = e.target.files;
    if (!uploadFiles || uploadFiles.length === 0) return;
    setUploading(true);
    const formData = new FormData();
    Array.from(uploadFiles).forEach(f => formData.append('file', f));
    await apiFetch('/api/admin/media/upload', { method: 'POST', body: formData, headers: {} });
    e.target.value = '';
    setUploading(false);
    await Promise.all([load(), loadStats()]);
  };

  const remove = async (key: string) => {
    if (!confirm(`Delete "${key}"?`)) return;
    await apiFetch(`/api/admin/media/${key}`, { method: 'DELETE' });
    await Promise.all([load(), loadStats()]);
  };

  const bulkDelete = async () => {
    if (!selected.size || !confirm(`Delete ${selected.size} file(s)?`)) return;
    setDeleting(true);
    await Promise.all(Array.from(selected).map(k => apiFetch(`/api/admin/media/${k}`, { method: 'DELETE' })));
    setSelected(new Set());
    setDeleting(false);
    await Promise.all([load(), loadStats()]);
  };

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopied(url);
    setTimeout(() => setCopied(null), 1500);
  };

  const toggleSelect = (k: string) => setSelected(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const filtered = files.filter(f => {
    if (filter === 'image' && !isImage(f.key)) return false;
    if (filter === 'other' && isImage(f.key)) return false;
    if (search && !f.key.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totalSize = filtered.reduce((s, f) => s + (f.size || 0), 0);
  const imageCount = files.filter(f => isImage(f.key)).length;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-violet-500 to-purple-600 bg-clip-text text-transparent">Media Library</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{files.length} files · {formatBytes(files.reduce((s, f) => s + (f.size || 0), 0))} total</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { load(); loadStats(); }} className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <label className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-xl text-sm font-medium cursor-pointer hover:opacity-90 transition shadow">
            {uploading ? <RefreshCw size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? 'Uploading…' : 'Upload Files'}
            <input type="file" multiple className="hidden" onChange={handleUpload} accept="image/*,video/*,.pdf,.svg" />
          </label>
        </div>
      </div>

      {/* Stats section */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200/60 dark:border-gray-700/60 p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-900 dark:text-gray-100 text-sm flex items-center gap-2">
            <TrendingUp size={16} className="text-violet-500" /> Media Stats
          </h2>
          <div className="flex gap-1">
            {STAT_RANGES.map(r => (
              <button key={r.key} onClick={() => setStatsRange(r.key)}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium transition ${statsRange === r.key ? 'bg-violet-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3 text-center border border-emerald-100 dark:border-emerald-800">
            <CheckCircle size={20} className="mx-auto mb-1 text-emerald-500" />
            <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">{stats?.current_files ?? files.length}</p>
            <p className="text-xs text-emerald-600 dark:text-emerald-500 mt-0.5">Active Files</p>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 text-center border border-blue-100 dark:border-blue-800">
            <Upload size={20} className="mx-auto mb-1 text-blue-500" />
            <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">{stats?.total_uploads ?? '—'}</p>
            <p className="text-xs text-blue-600 dark:text-blue-500 mt-0.5">Total Uploaded</p>
          </div>
          <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-3 text-center border border-red-100 dark:border-red-800">
            <XCircle size={20} className="mx-auto mb-1 text-red-500" />
            <p className="text-2xl font-bold text-red-700 dark:text-red-400">{stats?.total_deletes ?? '—'}</p>
            <p className="text-xs text-red-600 dark:text-red-500 mt-0.5">Total Deleted</p>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3 text-center border border-purple-100 dark:border-purple-800">
            <Image size={20} className="mx-auto mb-1 text-purple-500" />
            <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">{imageCount}</p>
            <p className="text-xs text-purple-600 dark:text-purple-500 mt-0.5">Images</p>
          </div>
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 text-center border border-amber-100 dark:border-amber-800">
            <TrendingUp size={20} className="mx-auto mb-1 text-amber-500" />
            <p className="text-2xl font-bold text-amber-700 dark:text-amber-400">{stats?.range_uploads ?? '—'}</p>
            <p className="text-xs text-amber-600 dark:text-amber-500 mt-0.5">Uploaded ({STAT_RANGES.find(r => r.key === statsRange)?.label})</p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700/40 rounded-xl p-3 text-center border border-gray-100 dark:border-gray-700">
            <HardDrive size={20} className="mx-auto mb-1 text-gray-500" />
            <p className="text-lg font-bold text-gray-700 dark:text-gray-300">{formatBytes(files.reduce((s, f) => s + (f.size || 0), 0))}</p>
            <p className="text-xs text-gray-500 mt-0.5">Total Size</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search files…"
          className="flex-1 min-w-[200px] px-4 py-2 rounded-xl border border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm"
        />
        <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl">
          {(['all', 'image', 'other'] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${filter === f ? 'bg-white dark:bg-gray-700 shadow text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}>
              {f === 'all' ? `All (${files.length})` : f === 'image' ? `🖼️ Images (${imageCount})` : `📄 Other (${files.length - imageCount})`}
            </button>
          ))}
        </div>
        {selected.size > 0 && (
          <button onClick={bulkDelete} disabled={deleting}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 disabled:opacity-50">
            <Trash2 size={14} /> {deleting ? 'Deleting…' : `Delete (${selected.size})`}
          </button>
        )}
        <span className="text-xs text-gray-400 dark:text-gray-500">{filtered.length} files · {formatBytes(totalSize)}</span>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="py-16 text-center text-gray-400">
          <RefreshCw size={24} className="animate-spin mx-auto mb-3" />
          <p>Loading…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-gray-400">
          <Image size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">{files.length === 0 ? 'No files uploaded yet.' : 'No files match your filter.'}</p>
          {files.length === 0 && <p className="text-sm mt-1">Upload your first file using the button above.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {filtered.map(file => {
            const img = isImage(file.key);
            const isSel = selected.has(file.key);
            return (
              <div key={file.key}
                className={`group relative rounded-2xl border-2 overflow-hidden bg-gray-50 dark:bg-gray-800/60 cursor-pointer transition-all ${isSel ? 'border-violet-500 shadow-lg shadow-violet-500/20' : 'border-gray-200 dark:border-gray-700 hover:border-violet-300'}`}
                onClick={() => toggleSelect(file.key)}>
                {/* Preview */}
                <div className="aspect-square flex items-center justify-center overflow-hidden bg-gray-100 dark:bg-gray-700">
                  {img ? (
                    <img src={file.url} alt={file.key} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="flex flex-col items-center gap-1 p-3 text-gray-400">
                      <span className="text-3xl">{file.key.endsWith('.pdf') ? '📄' : file.key.endsWith('.svg') ? '🎨' : '📁'}</span>
                      <span className="text-[9px] font-mono uppercase">{file.key.split('.').pop()}</span>
                    </div>
                  )}
                </div>

                {/* Selection indicator */}
                {isSel && (
                  <div className="absolute top-2 left-2 w-5 h-5 bg-violet-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-[10px]">✓</span>
                  </div>
                )}

                {/* Info overlay */}
                <div className="p-2">
                  <p className="text-[10px] font-mono text-gray-600 dark:text-gray-400 truncate">{file.key}</p>
                  <p className="text-[9px] text-gray-400 mt-0.5">{formatBytes(file.size || 0)}</p>
                </div>

                {/* Actions (show on hover, stop propagation) */}
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={e => { e.stopPropagation(); copyUrl(file.url); }}
                    title="Copy URL"
                    className="w-6 h-6 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg flex items-center justify-center text-gray-500 hover:text-blue-600 shadow text-xs"
                  >
                    {copied === file.url ? '✓' : '🔗'}
                  </button>
                  <button
                    onClick={e => { e.stopPropagation(); remove(file.key); }}
                    title="Delete"
                    className="w-6 h-6 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg flex items-center justify-center text-red-400 hover:text-red-600 shadow"
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
